import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  LinearProgress, MenuItem, Select, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowBackFill     from '@iconify/icons-eva/arrow-back-fill';
import messageSquareFill from '@iconify/icons-eva/message-square-fill';
import refreshFill       from '@iconify/icons-eva/refresh-fill';
import Page              from '../Page';
import PageHeader        from './shared/PageHeader';
import { getCampaignOverview, sendCompanyToReview } from '../../utils/outreachApi';
import { showErrorToast, showToast }                from '../../utils/Toast/SnackbarProvider';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const pct = (n, total) => (total > 0 ? Math.round((n / total) * 100) : 0);

// ─── HeroTile — top-row KPI card ─────────────────────────────────────────────
const HeroTile = ({ label, value, sub, color }) => (
  <Box
    sx={{
      flex: 1, minWidth: 160, px: 2, py: 1.5,
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
      bgcolor: 'background.paper',
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{ fontWeight: 700, lineHeight: 1.1, color: color || 'text.primary' }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ─── FunnelStep — one row of the conversion funnel ───────────────────────────
const FunnelStep = ({ label, value, total, isLast }) => {
  const p = pct(value, total);
  return (
    <Box sx={{ mb: isLast ? 0 : 1.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {value}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({p}%)
          </Typography>
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={p}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
const CampaignOverview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Per-row toggle for the failed-validation companies list.
  const [expandedQueueId, setExpandedQueueId] = useState(null);
  const [reasonFilter, setReasonFilter] = useState('ALL');
  const [sendingReviewId, setSendingReviewId] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Filter the failed-validation list server-side so it works across the
      // whole set (the list is capped at 200 rows per request).
      const params = reasonFilter !== 'ALL' ? { failed_reason: reasonFilter } : {};
      const { data: payload } = await getCampaignOverview(id, params);
      setData(payload);
    } catch (err) {
      showErrorToast(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, reasonFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSendToReview = async (company) => {
    const cid = company.company_id;
    const name = company.company_name || `Company #${cid}`;
    const hint = 'If this company already has a contact on file it goes straight to the '
               + 'review queue. If not, it will re-run enrichment (this uses Apollo credits) '
               + 'and only enters the queue if a contact is found.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send "${name}" to the review queue?\n\n${hint}`)) return;
    setSendingReviewId(cid);
    try {
      const { data } = await sendCompanyToReview(id, cid);
      if (data.mode === 'instant' || data.status === 'in_review') {
        showToast(`"${name}" sent to the review queue.`, 'success');
      } else if (data.status === 'already_active') {
        showToast(`"${name}" is already in the pipeline (${data.entry_status}).`, 'info');
      } else {
        showToast(`Re-enriching "${name}" — it will appear in review if a contact is found.`, 'info');
      }
      await load();   // refresh counts + list
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSendingReviewId(null);
    }
  };

  if (loading) {
    return (
      <Page title="Campaign overview">
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </Page>
    );
  }
  if (!data) {
    return (
      <Page title="Campaign overview">
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Alert severity="error">Failed to load campaign overview.</Alert>
        </Container>
      </Page>
    );
  }

  // ── Derive funnel values from raw counts ─────────────────────────────────
  const qc = data.queue?.by_status || {};
  const sends = data.sends || {};

  // Prefer the backend's authoritative per-company validation block. It counts
  // distinct companies across queue + invalid + newsletter, so it doesn't
  // undercount pipeline-invalidated companies whose queue row was never created
  // / got cleaned up (the old queue-only counts showed "Queued 5 / Failed 3"
  // for a campaign that really processed ~100 companies). Falls back to the
  // legacy queue-only derivation for older API responses.
  const v = data.validation || null;

  const legacyInflight = (qc.PENDING || 0) + (qc.ENRICHING || 0) + (qc.SCORING || 0)
                       + (qc.GENERATING || 0) + (qc.REVIEW || 0);

  const queued    = v ? v.queued : (data.queue?.total || 0);
  // Reviewed = companies a reviewer actually acted on (reviewed_by set), NOT
  // anything that merely reached a terminal pipeline stage (auto-disqualified
  // rows are not human reviews).
  const reviewed  = v ? v.reviewed : ((data.queue?.total || 0) - legacyInflight);
  const approved  = v ? v.approved : ((qc.APPROVED || 0) + (qc.ENROLLED || 0));
  const enrolled  = data.enrollment?.total || 0;

  // Validation-specific buckets (the headline numbers for the admin).
  // Passed = survived validation (reached REVIEW or beyond, minus any later
  //          invalidated). Failed = invalidated by the pipeline or rejected
  //          (auto-marked invalid + newsletter).
  const passedValidation = v ? v.passed
                             : ((qc.REVIEW || 0) + (qc.APPROVED || 0)
                                + (qc.REJECTED || 0) + (qc.ENROLLED || 0));
  const failedValidation = v ? v.failed
                             : ((qc.DISQUALIFIED || 0) + (qc.FAILED || 0));
  // Pass rate = of the companies that finished validation, how many passed.
  const validationTotal  = passedValidation + failedValidation;
  const passRate         = pct(passedValidation, validationTotal);

  const sumChannelSends = (ch) => {
    const row = sends[ch] || {};
    return (row.SENT || 0) + (row.DELIVERED || 0) + (row.OPENED || 0) + (row.REPLIED || 0);
  };
  const sentTotal = sumChannelSends('EMAIL')
                  + sumChannelSends('LINKEDIN_INVITE')
                  + sumChannelSends('LINKEDIN_MESSAGE');
  const replied   = data.replies?.total || 0;
  const replyRate = pct(replied, sentTotal);

  // Per-company failed-validation list (each row = one company + its
  // specific reason text). The aggregated count list was confusing; admins
  // want to see which company failed and why.
  // The failed list is already filtered + capped server-side (see `load`).
  const failedCompanies = data.queue?.failed_companies || [];
  // Full-set reason breakdown from the backend (accurate counts, not limited
  // by the 200-row list cap), for the filter dropdown.
  const failedReasons = data.queue?.failed_reasons || [];
  // A stable per-row id: invalid-only rows have no queue_id, so fall back to
  // company_id (rows are deduped by company) to avoid collisions in the key /
  // expand state.
  const rowId = (c) => c.queue_id ?? c.invalid_id ?? c.company_id;

  return (
    <Page title={`Overview — ${data.campaign_name}`}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <PageHeader
          title={data.campaign_name}
          subtitle={
            <>
              <Chip
                label={data.status}
                size="small"
                sx={{ mr: 1, textTransform: 'uppercase', fontWeight: 700 }}
              />
              {data.started_at ? `Started ${fmt(data.started_at)}` : 'Not started'}
              {data.last_activity_at ? ` · Last activity ${fmt(data.last_activity_at)}` : ''}
            </>
          }
          actions={
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<Icon icon={arrowBackFill} width={16} />}
                onClick={() => navigate('/outreach/campaigns')}
              >
                All
              </Button>
              <Button
                startIcon={<Icon icon={refreshFill} width={16} />}
                onClick={load}
                disabled={refreshing}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon icon={messageSquareFill} width={16} />}
                onClick={() => navigate(`/outreach/campaigns/${id}/conversations`)}
              >
                View conversations
              </Button>
            </Stack>
          }
        />

        {refreshing && <LinearProgress sx={{ mb: 2 }} />}

        {/* Hero tiles — validation outcomes are the headline numbers */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
          <HeroTile label="Queued" value={queued} />
          <HeroTile
            label="Passed validation"
            value={passedValidation}
            sub={validationTotal > 0 ? `${passRate}% pass rate` : 'awaiting first results'}
            color={passedValidation > 0 ? 'success.dark' : undefined}
          />
          <HeroTile
            label="Failed validation"
            value={failedValidation}
            sub={failedValidation > 0 ? 'auto-marked invalid + newsletter' : 'none yet'}
            color={failedValidation > 0 ? 'error.main' : undefined}
          />
          <HeroTile
            label="Replied"
            value={replied}
            sub={sentTotal > 0 ? `${replyRate}% reply rate · ${sentTotal} sent` : 'no sends yet'}
            color={replied > 0 ? 'success.dark' : undefined}
          />
        </Stack>

        {/* Two columns: funnel + drop reasons */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
          {/* Funnel */}
          <Card variant="outlined" sx={{ flex: 1.4 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Conversion funnel
              </Typography>
              {queued === 0 ? (
                <Alert severity="info">No companies have been queued yet.</Alert>
              ) : (
                <>
                  <FunnelStep label="Queued"   value={queued}   total={queued} />
                  <FunnelStep label="Reviewed" value={reviewed} total={queued} />
                  <FunnelStep label="Approved" value={approved} total={queued} />
                  <FunnelStep label="Enrolled" value={enrolled} total={queued} />
                  <FunnelStep label="Sent"     value={sentTotal} total={queued} />
                  <FunnelStep label="Replied"  value={replied}  total={queued} isLast />
                </>
              )}
            </CardContent>
          </Card>

          {/* Failed validation — list of companies, click for reason */}
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Failed validation
                </Typography>
                {failedValidation > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {failedValidation} companies
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                Auto-marked invalid + added to the newsletter audience. Click a row to see why.
              </Typography>

              {failedValidation > 0 && (
                <Select
                  size="small"
                  value={reasonFilter}
                  onChange={(e) => { setReasonFilter(e.target.value); setExpandedQueueId(null); }}
                  sx={{ mb: 1.25, minWidth: 240, fontSize: '0.8rem' }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: '0.8rem' }}>
                    All reasons ({failedValidation})
                  </MenuItem>
                  {failedReasons.map((r) => (
                    <MenuItem key={r.label} value={r.label} sx={{ fontSize: '0.8rem' }}>
                      {r.label} ({r.count})
                    </MenuItem>
                  ))}
                </Select>
              )}

              {failedValidation === 0 ? (
                <Alert severity={validationTotal > 0 ? 'success' : 'info'} sx={{ py: 0.5 }}>
                  {validationTotal > 0
                    ? 'No validation failures so far — every processed company passed.'
                    : 'No companies have finished validation yet.'}
                </Alert>
              ) : (
                <Box
                  sx={{
                    maxHeight: 360, overflowY: 'auto',
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  }}
                >
                  {failedCompanies.length === 0 ? (
                    <Box sx={{ px: 1.5, py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {refreshing ? 'Loading…' : 'No companies failed for this reason.'}
                      </Typography>
                    </Box>
                  ) : failedCompanies.map((c, i) => {
                    const id = rowId(c);
                    const open = expandedQueueId === id;
                    return (
                      <Box
                        key={id}
                        sx={{
                          borderBottom: i === failedCompanies.length - 1 ? 'none' : '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Stack
                          direction="row" alignItems="center" spacing={1}
                          onClick={() => setExpandedQueueId(open ? null : id)}
                          sx={{
                            px: 1.5, py: 1,
                            cursor: 'pointer',
                            bgcolor: open ? 'action.hover' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                            {c.company_name || `Company #${c.company_id}`}
                          </Typography>
                          <Chip
                            size="small"
                            label={c.source}
                            color={c.status === 'FAILED' ? 'error' : 'warning'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Stack>
                        {open && (
                          <Box sx={{ px: 1.5, pb: 1.25, pt: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', lineHeight: 1.4, mb: 0.5 }}
                            >
                              {c.reason || 'No reason recorded.'}
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                              {c.pipeline_stage && (
                                <Typography variant="caption" color="text.secondary">
                                  Stage: {c.pipeline_stage}
                                </Typography>
                              )}
                              {c.failed_at && (
                                <Typography variant="caption" color="text.secondary">
                                  · {fmt(c.failed_at)}
                                </Typography>
                              )}
                              {c.company_id && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={sendingReviewId === c.company_id}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    handleSendToReview(c);
                                  }}
                                  sx={{ ml: 'auto', fontSize: '0.7rem' }}
                                >
                                  {sendingReviewId === c.company_id ? 'Sending…' : 'Send to review'}
                                </Button>
                              )}
                              {c.company_id && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    navigate(`/companies/${c.company_id}`);
                                  }}
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  Open company
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>

        {/* Per-BA table */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Per-BA activity
            </Typography>
            {(data.per_pm || []).length === 0 ? (
              <Alert severity="info">No contacts assigned to any BA yet.</Alert>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>BA</strong></TableCell>
                      <TableCell align="right">Assigned</TableCell>
                      <TableCell align="right">Approved</TableCell>
                      <TableCell align="right">Sent</TableCell>
                      <TableCell align="right">Replied</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.per_pm.map((r) => (
                      <TableRow key={r.pm_id}>
                        <TableCell>{r.pm_name}</TableCell>
                        <TableCell align="right">{r.queue_assigned}</TableCell>
                        <TableCell align="right">{r.approved}</TableCell>
                        <TableCell align="right">{r.sent}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: r.replied > 0 ? 'success.dark' : 'inherit',
                            fontWeight: r.replied > 0 ? 700 : 400,
                          }}
                        >
                          {r.replied}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default CampaignOverview;
